import type { Observable as ObservableType } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { tap } from 'rxjs/operators/tap';
import { createSessionExpirableStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/session-storage/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	CACHE_KEYS,
	STORAGE_PROVIDER_PREFIX,
} from '../../view/whiteboards-entrypoint-experiment/constants.tsx';
import {
	fetchUserPropertyKeys,
	fetchSiteTraits,
} from '../../rest/whiteboards-entrypoint-experiment/index.tsx';

export const hasXFlowFromWhiteboardsTouchpointService = (
	cloudId: string,
): ObservableType<boolean> => {
	const expirableStorage = createSessionExpirableStorageProvider(STORAGE_PROVIDER_PREFIX);
	const cachedValue = expirableStorage.get(
		CACHE_KEYS.HAS_XFLOW_FROM_WHITEBOARDS_TOUCHPOINT_TAP_TRAIT_KEY,
	);
	if (cachedValue !== null && cachedValue !== undefined) {
		return of(cachedValue);
	}

	return fetchSiteTraits(cloudId).pipe(
		map((response) =>
			response.attributes.some(
				(attribute) =>
					attribute?.name === CACHE_KEYS.HAS_XFLOW_FROM_WHITEBOARDS_TOUCHPOINT_TAP_TRAIT_KEY,
			),
		),
		tap((hasCrossFlowed) => {
			expirableStorage.set(
				CACHE_KEYS.HAS_XFLOW_FROM_WHITEBOARDS_TOUCHPOINT_TAP_TRAIT_KEY,
				hasCrossFlowed,
			);
		}),
		catchError((error) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'backlog.has-cross-flowed-from-whiteboards-service.failure',
					packageName: 'jiraSoftwareBacklog',
					teamName: 'starburst',
				},
			});
			return of(false);
		}),
	);
};

const ONE_DAY = 1000 * 60 * 60 * 24;

export const loadUserPropertyKeysService = (accountId: string): ObservableType<string[] | null> => {
	const expirableStorage = createSessionExpirableStorageProvider(STORAGE_PROVIDER_PREFIX);
	const cachedValue = expirableStorage.get(CACHE_KEYS.USER_PROPERTIES);
	if (cachedValue !== null && cachedValue !== undefined) {
		return of(cachedValue);
	}

	return fetchUserPropertyKeys(accountId).pipe(
		map((response) => response.keys.map((value) => value.key)),
		tap((keys) => {
			expirableStorage.set(CACHE_KEYS.USER_PROPERTIES, keys, Date.now() + ONE_DAY);
		}),
		catchError(() => of(null)),
	);
};
